import React from 'react';
import Image from 'next/image';

interface PaginationProps {
  totalPages: number;
  currentPage: number;
  handlePageChange: (page: number) => void;
}

const Pagination = ({
  totalPages,
  currentPage,
  handlePageChange
}: PaginationProps) => {
  const renderPageNumbers = () => {
    const pageNumbers: (number | string)[] = [];

    // Always show the first page
    pageNumbers.push(1);

    // If total pages are 5 or less, show all page numbers
    if (totalPages <= 5) {
      for (let i = 2; i < totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Show pages near the start (when currentPage <= 3)
      if (currentPage <= 3) {
        for (let i = 2; i <= 3; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
      }
      // Show middle pages like 1 ... 3 4 ... totalPages
      else if (currentPage > 3 && currentPage < totalPages - 2) {
        pageNumbers.push('...');
        pageNumbers.push(currentPage - 1);
        pageNumbers.push(currentPage);
        pageNumbers.push('...');
      }
      // Show pages near the end (when currentPage >= totalPages - 2)
      else {
        pageNumbers.push('...');
        for (let i = totalPages - 2; i < totalPages; i++) {
          pageNumbers.push(i);
        }
      }
    }

    // Always show the last page
    pageNumbers.push(totalPages);

    return pageNumbers.map((pageNumber, index) => (
      <button
        key={index}
        className={`text-xs sm:text-sm md:text-base w-[24px] h-[24px] sm:w-[26px] sm:h-[26px] xl:w-[30px] xl:h-[30px] rounded-full ${
          currentPage === pageNumber
            ? 'bg-primary text-white'
            : 'bg-transparent text-black'
        }`}
        onClick={() =>
          handlePageChange(
            typeof pageNumber === 'number' ? pageNumber : currentPage
          )
        }
      >
        {pageNumber}
      </button>
    ));
  };

  return (
    <div className="flex items-center my-14 md:my-4 justify-center xxsm:gap-2 xsm:gap-2 mmsm:gap-3 sm:gap-6 sm:py-3 py-0">
      <button
        className="flex justify-center items-center"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <Image
          src={
            currentPage === 1
              ? '/gray-circled-left-arrow.svg'
              : '/red-circled-left-arrow.svg'
          }
          width={30}
          height={30}
          alt="left-button"
          className="w-[24px] h-[24px] sm:w-[26px] sm:h-[26px] xl:w-[30px] xl:h-[30px]"
        />
      </button>
      <div className="flex flex-row xxsm:gap-2 mmsm:gap-2 sm:gap-3">
        {renderPageNumbers()}
      </div>
      <button
        className="flex justify-center items-center"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <Image
          src={
            currentPage === totalPages
              ? '/gray-circled-right-arrow.svg'
              : '/red-circled-right-arrow.svg'
          }
          width={30}
          height={30}
          alt="right-button"
          className="w-[24px] h-[24px] sm:w-[26px] sm:h-[26px] xl:w-[30px] xl:h-[30px]"
        />
      </button>
    </div>
  );
};

export default Pagination;
